import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import AddIcon from '@mui/icons-material/Add';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PercentIcon from '@mui/icons-material/Percent';
import EditIcon from '@mui/icons-material/Edit';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { BountyVerteilung as BountyVerteilungData, SchwachstelleList } from '../../types';
import { bountyService } from '../../services/bountyService';
import { formatSeverityLower, getKritikalitaetChipSx } from '../../utils/severity';
import { formatSchwachstellenStatusDisplay } from '../../utils/schwachstellenStatus';
import { accordionSummarySx, accordionSx } from './auditDetailShared';
import { getStatusColor } from './auditDetailUtils';
import { navigableProps } from '../../utils/navigation';
import { findingChapterLabel } from '../../utils/schwachstelleReferenz';
import { MitarbeiterKurzprofilLink } from '../../components/common/MitarbeiterKurzprofilLink';
import SandboxFreigabeToggle from '../../components/common/SandboxFreigabeToggle';

interface AuditFindingsSectionProps {
  isDark: boolean;
  expanded: boolean;
  schwachstellen: SchwachstelleList[];
  loading: boolean;
  onToggle: (_: React.SyntheticEvent, isExpanded: boolean) => void;
  onCreate: () => void;
  onEdit: (schwachstelleId: number) => void;
  onTransfer: (schwachstelle: SchwachstelleList) => void;
  onDelete: (schwachstelleId: number) => void;
  onDragEnd: (result: DropResult) => void;
  canTransferSchwachstelle: boolean;
  canDeleteSchwachstelle: (schwachstelle: SchwachstelleList) => boolean;
}

const bountyVerteilungCache = new Map<number, BountyVerteilungData>();

function BountyVerteilungHoverTooltip({
  schwachstelleId,
  children,
}: {
  schwachstelleId: number;
  children: React.ReactElement;
}) {
  const [verteilung, setVerteilung] = useState<BountyVerteilungData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleOpen = useCallback(() => {
    if (bountyVerteilungCache.has(schwachstelleId)) {
      setVerteilung(bountyVerteilungCache.get(schwachstelleId)!);
      setLoading(false);
      setError(false);
      return;
    }
    setLoading(true);
    setError(false);
    setVerteilung(null);
    bountyService
      .get(schwachstelleId)
      .then((v) => {
        bountyVerteilungCache.set(schwachstelleId, v);
        setVerteilung(v);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [schwachstelleId]);

  return (
    <Tooltip
      enterDelay={400}
      enterNextDelay={250}
      onOpen={handleOpen}
      slotProps={{
        tooltip: {
          sx: { maxWidth: 360 },
        },
      }}
      title={
        <Box sx={{ py: 0.25, minWidth: 200 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {!loading && error && (
            <Typography variant="caption">Verteilung konnte nicht geladen werden.</Typography>
          )}
          {!loading && !error && verteilung && verteilung.anteile.length > 0 && (
            <Box component="ul" sx={{ m: 0, pl: 2, pr: 0.5, py: 0.25 }}>
              {verteilung.anteile.map((a) => (
                <li key={a.mitarbeiter_id}>
                  <Typography variant="body2" component="div" sx={{ fontSize: '0.8125rem' }}>
                    <MitarbeiterKurzprofilLink mitarbeiterId={a.mitarbeiter_id}>
                      {a.mitarbeiter.vorname} {a.mitarbeiter.nachname}
                    </MitarbeiterKurzprofilLink>
                    <Box component="span" sx={{ fontWeight: 700, ml: 0.75 }}>
                      {a.anteil_prozent}%
                    </Box>
                  </Typography>
                </li>
              ))}
            </Box>
          )}
          {!loading && !error && verteilung && verteilung.anteile.length === 0 && (
            <Typography variant="caption">Keine Verteilung hinterlegt.</Typography>
          )}
        </Box>
      }
      arrow
    >
      {children}
    </Tooltip>
  );
}

const AuditFindingsSection: React.FC<AuditFindingsSectionProps> = ({
  isDark,
  expanded,
  schwachstellen,
  loading,
  onToggle,
  onCreate,
  onEdit,
  onTransfer,
  onDelete,
  onDragEnd,
  canTransferSchwachstelle,
  canDeleteSchwachstelle,
}) => {
  const navigate = useNavigate();

  return (
  <Accordion expanded={expanded} onChange={onToggle} sx={accordionSx(isDark)}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx(isDark)}>
      <BugReportOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        Schwachstellen
      </Typography>
      {schwachstellen.length > 0 && (
        <Chip
          label={schwachstellen.length}
          size="small"
          sx={{
            ml: 1,
            fontWeight: 700,
            fontSize: '0.7rem',
            height: 22,
            minWidth: 28,
            bgcolor: isDark ? 'rgba(239,83,80,0.12)' : 'rgba(239,83,80,0.08)',
            color: '#ef5350',
            border: '1px solid rgba(239,83,80,0.3)',
          }}
        />
      )}
    </AccordionSummary>
    <AccordionDetails sx={{ px: 2, py: 2 }}>
      {loading ? (
        <Box sx={{ py: 2 }}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={48} sx={{ mb: 1 }} />
          ))}
        </Box>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={onCreate}>
              Schwachstelle hinzufügen
            </Button>
          </Box>

          {schwachstellen.length === 0 ? (
            <Alert severity="info">Keine Schwachstellen für dieses Audit gefunden.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 52, pr: 0.5 }}>Kap.</TableCell>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell>Titel</TableCell>
                    <TableCell>Klasse</TableCell>
                    <TableCell>CWE</TableCell>
                    <TableCell>Kritikalität</TableCell>
                    <TableCell>CVSS Score</TableCell>
                    <TableCell>Host</TableCell>
                    <TableCell>Reporter</TableCell>
                    <TableCell>Bounty</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Kommentare" arrow>
                        <ChatBubbleOutlineIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="audit-findings">
                    {(dropProvided) => (
                      <TableBody ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                        {schwachstellen.map((schwachstelle, index) => (
                          <Draggable
                            key={schwachstelle.id}
                            draggableId={`sv-${schwachstelle.id}`}
                            index={index}
                          >
                            {(dragProvided, dragSnapshot) => {
                              const detailPath = `/schwachstellen/${schwachstelle.id}`;
                              const nav = navigableProps(navigate, detailPath);
                              const dp = dragProvided.draggableProps as React.HTMLAttributes<HTMLTableRowElement>;
                              return (
                              <TableRow
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                hover
                                sx={{
                                  cursor: 'pointer',
                                  ...(dragSnapshot.isDragging
                                    ? { bgcolor: isDark ? 'rgba(61,212,126,0.12)' : 'rgba(61,212,126,0.08)' }
                                    : {}),
                                }}
                                onClick={(e) => {
                                  dp.onClick?.(e);
                                  nav.onClick(e);
                                }}
                                onMouseDown={(e) => {
                                  dp.onMouseDown?.(e);
                                  nav.onMouseDown(e);
                                }}
                                onMouseUp={(e) => {
                                  dp.onMouseUp?.(e);
                                  nav.onMouseUp(e);
                                }}
                              >
                                <TableCell
                                  sx={{
                                    pr: 0.5,
                                    fontWeight: 600,
                                    fontVariantNumeric: 'tabular-nums',
                                    color: 'text.secondary',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {findingChapterLabel(index + 1)}
                                </TableCell>
                                <TableCell
                                  onClick={(event) => event.stopPropagation()}
                                  onMouseDown={(e) => {
                                    if (e.button === 1) e.stopPropagation();
                                  }}
                                  onMouseUp={(e) => {
                                    if (e.button === 1) e.stopPropagation();
                                  }}
                                  sx={{ width: 48, color: 'text.secondary' }}
                                >
                                  <IconButton
                                    size="small"
                                    {...dragProvided.dragHandleProps}
                                    sx={{ cursor: 'grab' }}
                                    aria-label="Findings-Reihenfolge verschieben"
                                  >
                                    <DragIndicatorIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>{schwachstelle.titel}</TableCell>
                                <TableCell>{schwachstelle.schwachstellenklasse?.name_de || '-'}</TableCell>
                                <TableCell>
                                  {schwachstelle.cwe ? (
                                    <Tooltip title={schwachstelle.cwe.name} arrow placement="top">
                                      <span>CWE-{schwachstelle.cwe.id}</span>
                                    </Tooltip>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell>
                                  {schwachstelle.kritikalitaet ? (
                                    <Chip
                                      label={formatSeverityLower(schwachstelle.kritikalitaet)}
                                      size="small"
                                      sx={getKritikalitaetChipSx(schwachstelle.kritikalitaet)}
                                    />
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell>
                                  {schwachstelle.cvss31_score ? (
                                    <Tooltip
                                      title={schwachstelle.cvss31_vector || 'Kein Vektor'}
                                      arrow
                                      placement="top"
                                    >
                                      <span style={{ fontWeight: 600 }}>{schwachstelle.cvss31_score}</span>
                                    </Tooltip>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell>{schwachstelle.host || '-'}</TableCell>
                                <TableCell>
                                  {schwachstelle.reporter
                                    ? `${schwachstelle.reporter.vorname} ${schwachstelle.reporter.nachname}`
                                    : '-'}
                                </TableCell>
                                <TableCell
                                  onClick={(e) => {
                                    if (schwachstelle.has_split_bounty) e.stopPropagation();
                                  }}
                                >
                                  {schwachstelle.reporter && schwachstelle.has_split_bounty ? (
                                    <BountyVerteilungHoverTooltip schwachstelleId={schwachstelle.id}>
                                      <Box
                                        component="span"
                                        sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          p: 0.3,
                                          borderRadius: '50%',
                                          bgcolor: 'action.hover',
                                          flexShrink: 0,
                                          cursor: 'help',
                                        }}
                                      >
                                        <PercentIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                                      </Box>
                                    </BountyVerteilungHoverTooltip>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={formatSchwachstellenStatusDisplay(schwachstelle.status?.name)}
                                    size="small"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: '0.7rem',
                                      bgcolor: `${getStatusColor(schwachstelle.status?.name)}20`,
                                      color: getStatusColor(schwachstelle.status?.name),
                                      border: `1px solid ${getStatusColor(schwachstelle.status?.name)}40`,
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  {(schwachstelle.kommentar_anzahl ?? 0) > 0 ? (
                                    <Chip
                                      icon={<ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />}
                                      label={schwachstelle.kommentar_anzahl}
                                      size="small"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        height: 22,
                                        minWidth: 28,
                                        bgcolor: isDark ? 'rgba(33,150,243,0.12)' : 'rgba(33,150,243,0.08)',
                                        color: '#2196f3',
                                        border: '1px solid rgba(33,150,243,0.3)',
                                      }}
                                    />
                                  ) : (
                                    <Typography variant="caption" color="text.disabled">
                                      —
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <SandboxFreigabeToggle
                                    kind="schwachstelle"
                                    id={schwachstelle.id}
                                    auditId={schwachstelle.audit_id}
                                    variant="icon"
                                  />
                                  <Tooltip title="Bearbeiten">
                                    <IconButton
                                      size="small"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        onEdit(schwachstelle.id);
                                      }}
                                      sx={{ color: 'text.secondary', '&:hover': { color: '#3dd47e' } }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {canTransferSchwachstelle && (
                                    <Tooltip title="In anderes Audit verschieben">
                                      <IconButton
                                        size="small"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          onTransfer(schwachstelle);
                                        }}
                                        sx={{ color: 'text.secondary', '&:hover': { color: '#42a5f5' } }}
                                      >
                                        <DriveFileMoveOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {canDeleteSchwachstelle(schwachstelle) && (
                                    <Tooltip title="Löschen">
                                      <IconButton
                                        size="small"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          onDelete(schwachstelle.id);
                                        }}
                                        sx={{ color: 'text.secondary', '&:hover': { color: '#ef5350' } }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                              );
                            }}
                          </Draggable>
                        ))}
                        {dropProvided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                </DragDropContext>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </AccordionDetails>
  </Accordion>
  );
};

export default AuditFindingsSection;
